import { ApiError } from './api-error';

export type PodErrorType =
  | 'DUPLICATE_POD_NAME'
  | 'INACTIVE_POD'
  | 'POD_NOT_FOUND'
  | 'INSUFFICIENT_PRIVILEGES'
  | 'VALIDATION_ERROR'
  | 'GENERIC';

export interface PodErrorClassification {
  type: PodErrorType;
  /** i18n key for the error message. Undefined for VALIDATION_ERROR (use fallback with backend message). */
  messageKey: string | undefined;
  /** Fallback display string. For VALIDATION_ERROR, this is the raw backend message. */
  fallback: string;
}

/**
 * Classifies a pod mutation error into a typed classification.
 * Hooks use the `type` field to decide behavior (toast, callback, cache invalidation).
 * The `messageKey` and `fallback` fields are SUGGESTIONS -- hooks MAY override them
 * with operation-specific messages (e.g., useDeletePod uses its own i18n keys).
 *
 * @param error - Expected to be an ApiError instance; safely handles non-ApiError values.
 */
export const classifyPodError = (error: unknown): PodErrorClassification => {
  if (!(error instanceof ApiError)) {
    return {
      type: 'GENERIC',
      messageKey: 'labels.unableToSavePod',
      fallback: 'Unable to save Pod. Please try again.',
    };
  }

  const { status, message: rawMessage } = error;
  const message = rawMessage?.toLowerCase() ?? '';

  if (status === 400) {
    return {
      type: 'VALIDATION_ERROR',
      messageKey: undefined,
      fallback: error.message,
    };
  }

  if (status === 403) {
    return {
      type: 'INSUFFICIENT_PRIVILEGES',
      messageKey: 'labels.noPermissionPod',
      fallback: "You don't have permission to perform this action",
    };
  }

  if (status === 404) {
    return {
      type: 'POD_NOT_FOUND',
      messageKey: 'labels.podNotFound',
      fallback: 'Pod not found',
    };
  }

  if (status === 422 && message.includes('already exists')) {
    return {
      type: 'DUPLICATE_POD_NAME',
      messageKey: 'labels.podNameAlreadyExists',
      fallback: 'Pod name already exists in this agency',
    };
  }

  if (status === 422 && message.includes('inactive')) {
    return {
      type: 'INACTIVE_POD',
      messageKey: 'labels.inactivePodError',
      fallback:
        'This pod is inactive. Activate it first before editing configuration.',
    };
  }

  return {
    type: 'GENERIC',
    messageKey: 'labels.unableToSavePod',
    fallback: 'Unable to save Pod. Please try again.',
  };
};
